import { useCallback, useEffect, useState, useMemo } from 'react';
import { useAtom } from 'jotai';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AllPointOfInterestTemplate } from '../../types';
import { AddTemplateToStructureDialog } from './AddTemplateToStructureDialog';
import { StructuresList } from './StructuresList';
import { TemplateForm } from './TemplateForm';
import { GenericDialog } from '../shared/GenericDialog';
import {
  AllPointOfInterestTemplateDocument,
  GetVersionsDocument,
  PointOfInterestTemplate,
  PointOfInterestTemplateTypeEnum,
  TemplateTypeEnum,
  useAddMiServicesOverhaulFieldsToPoiTemplatesMutation,
  useAllPointOfInterestTemplateQuery,
} from '@/__generated__/graphql';
import { selectedPOIType as selectedPOITemplate } from './state';
import { TabPill } from './styles';
import { useDownloadJson } from './useDownloadJson';
import { useSnackBarMessage } from './utils';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { EmptyStatePlaceholder } from '../Analysis/shared/EmptyStatePlaceholder';
import { abyssColors } from '@/theme/colors';
import { BLISTER, PIT } from '@/constants';

type PoiTemplateProps = {
  structureId: string;
  structureName: string;
};

export const PoiTemplate = ({ structureId, structureName }: PoiTemplateProps) => {
  const [allPointOfInterestTemplates, setAllPointOfInterestTemplates] =
    useState<AllPointOfInterestTemplate>([]);
  const [selectedPoiType, setSelectedPoiType] = useAtom(selectedPOITemplate);
  const [emptyForm, setEmptyForm] = useState<boolean>(false);
  const [showStructuresList, setShowStructuresList] = useState<boolean>(false);
  const [selectedStructureId, setSelectedStructureId] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [
    isMiServicesOverhaulConfirmationDialogOpen,
    setIsMiServicesOverhaulConfirmationDialogOpen,
  ] = useState<boolean>(false);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const { data: poiTemplatesData } = useAllPointOfInterestTemplateQuery({
    variables: {
      structureId,
    },
  });

  useEffect(() => {
    if (poiTemplatesData?.allPointOfInterestTemplate) {
      setAllPointOfInterestTemplates(poiTemplatesData?.allPointOfInterestTemplate ?? []);
      setSelectedPoiType(poiTemplatesData?.allPointOfInterestTemplate[0]);
    }
  }, [poiTemplatesData?.allPointOfInterestTemplate, setSelectedPoiType]);

  const poiId = useMemo(() => {
    return (
      allPointOfInterestTemplates.find((template) => template.id === selectedPoiType?.id)?.id ?? ''
    );
  }, [allPointOfInterestTemplates, selectedPoiType]);

  const handleSelectedType = useCallback(
    (type: Partial<PointOfInterestTemplate>) => {
      setSelectedPoiType(type);
    },
    [setSelectedPoiType]
  );

  const addNewPoiTemplate = useCallback(() => {
    setShowStructuresList(false);
    setSelectedStructureId(structureId);
    setSelectedPoiType({
      name: '',
      accentColor: '',
      icon: { name: '', color: '#FFF' },
      fields: [],
      id: 'newType' + Date.now(),
      type: PointOfInterestTemplateTypeEnum.PointOfInterest,
    });
    setEmptyForm(true);
  }, [setSelectedPoiType, structureId]);

  const addTemplateFromAnotherStructure = useCallback(() => {
    setShowStructuresList(true);
  }, []);

  const handlePlatformClick = useCallback(
    (id: string) => {
      if (id === structureId) {
        showSnackBar(`You have already selected ${structureName}`, 'info');
      } else {
        setSelectedStructureId(id);
        setIsOpen(true);
      }
    },
    [showSnackBar, structureId, structureName]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const viewPoiTemplates = useCallback(() => {
    setShowStructuresList(false);
  }, []);

  const { downloadJson } = useDownloadJson({
    poiId,
    fileName: `${structureName}-${selectedPoiType?.name}-${Date.now().toString()}.json`,
  });

  const allTemplateNames = useMemo(() => {
    return allPointOfInterestTemplates.length > 0
      ? allPointOfInterestTemplates.map((template) => template.name.toLowerCase())
      : [];
  }, [allPointOfInterestTemplates]);

  const templateIds = useMemo(() => {
    return allPointOfInterestTemplates
      .filter(({ name }) => name.includes(BLISTER) || name.includes(PIT))
      .map(({ id }) => id);
  }, [allPointOfInterestTemplates]);

  const [addMiServicesOverhaulFields] = useAddMiServicesOverhaulFieldsToPoiTemplatesMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument, GetVersionsDocument],
  });

  const handleAddMiServicesOverhaulFields = useCallback(() => {
    addMiServicesOverhaulFields({
      variables: {
        input: {
          structureId,
          templateIds: templateIds ?? [],
        },
      },
      onCompleted: (response) => {
        if (response?.addMiServicesOverhaulFieldsToPoiTemplates) {
          showSnackBar('MI Services Overhaul Fields added successfully', 'success');
          setIsMiServicesOverhaulConfirmationDialogOpen(false);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`${errorResponse.message}`, 'error');
        setIsMiServicesOverhaulConfirmationDialogOpen(false);
      },
    });
  }, [addMiServicesOverhaulFields, showSnackBar, structureId, templateIds]);

  return (
    <Stack>
      <Stack
        direction="row"
        gap={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          pt: 4,
          alignItems: 'center',
        }}
      >
        {showStructuresList ? (
          <Typography variant="h6">Select a platform to copy template from</Typography>
        ) : (
          allPointOfInterestTemplates.map((type) => (
            <TabPill
              variant="text"
              size="small"
              selected={selectedPoiType?.id === type.id}
              key={type.id}
              onClick={() => handleSelectedType(type)}
            >
              {type.name}
            </TabPill>
          ))
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{
            ml: 'auto',
            flexShrink: 0,
          }}
        >
          <Button
            startIcon={showStructuresList ? <VisibilityIcon /> : <FileCopyIcon />}
            variant="contained"
            onClick={showStructuresList ? viewPoiTemplates : addTemplateFromAnotherStructure}
            sx={containedButtonStyle}
          >
            {showStructuresList ? 'View POI Templates' : 'Copy Template From Another Structure'}
          </Button>

          {selectedPoiType && !showStructuresList && (
            <>
              <Button
                startIcon={<AddCircleIcon />}
                variant="contained"
                sx={containedButtonStyle}
                onClick={() => setIsMiServicesOverhaulConfirmationDialogOpen(true)}
              >
                Add MI Services Overhaul Fields
              </Button>
              <Button
                startIcon={<AddCircleIcon />}
                variant="contained"
                onClick={addNewPoiTemplate}
                sx={containedButtonStyle}
              >
                Add New Template
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                variant="contained"
                onClick={downloadJson}
                sx={containedButtonStyle}
              >
                Download JSON
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {!showStructuresList &&
        (allPointOfInterestTemplates.length > 0 ? (
          <TemplateForm
            key={selectedPoiType?.id ?? ''}
            handleEmptyForm={(value) => setEmptyForm(value)}
            emptyForm={emptyForm}
            currentTemplateType={TemplateTypeEnum.PointOfInterest.toString()}
            structureId={structureId}
          />
        ) : (
          <Box
            margin="6.4rem 0"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <EmptyStatePlaceholder type="template" />
          </Box>
        ))}
      {showStructuresList && (
        <Box>
          <StructuresList onPlatformClick={handlePlatformClick} drawerOpen />
        </Box>
      )}
      {isOpen && (
        <AddTemplateToStructureDialog
          isOpen={isOpen}
          structureId={selectedStructureId}
          onClose={handleClose}
          structureName={structureName}
          currentStructureId={structureId}
          existingTemplates={allTemplateNames}
        />
      )}
      {isMiServicesOverhaulConfirmationDialogOpen && (
        <GenericDialog
          open={isMiServicesOverhaulConfirmationDialogOpen}
          onClose={() => setIsMiServicesOverhaulConfirmationDialogOpen(false)}
          title="Add MI Services Overhaul Fields"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to add MI Services Overhaul Fields to this deployment?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsMiServicesOverhaulConfirmationDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleAddMiServicesOverhaulFields()}
                sx={containedButtonStyle}
              >
                Confirm
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </Stack>
  );
};
